/* eslint-disable react/jsx-no-duplicate-props */
import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import { TextField, IconButton, Text } from "../../../includes";
import { IFloatInput } from "./types";
import { DEFAULT_INPUT_WIDTH, FONT_SIZE } from "./enums";
import { arr } from "./helpers";
import useIsDeployedVersion from "@/hooks/useIsDeployedVersion";

const FloatInput: React.FC<IFloatInput> = ({
  value,
  setValue,
  variant,
  max,
  min,
  disabled,
  type = "float",
  sx = {},
}) => {
  const theme = useTheme();

  const maxValue = max ?? 999999;
  const minValue = min ?? 0;

  const [inputWidth, setInputWidth] = useState(DEFAULT_INPUT_WIDTH);

  useEffect(() => {
    if (value) setInputWidth((arr(value).length + 1) * (FONT_SIZE - 2));
  }, [value]);

  return (
    <TextField
      disabled={disabled}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              sx={{ width: 24, height: 24 }}
              disabled={disabled}
              onClick={() => {
                if (value < maxValue) {
                  setValue(
                    Math.round((value + (type === "float" ? 0.1 : 1)) * 10) / 10
                  );
                }
              }}
              data-testid="plus-button"
            >
              <Text variant="body1">+</Text>
            </IconButton>
          </InputAdornment>
        ),
        startAdornment: (
          <InputAdornment position="start">
            <IconButton
              sx={{ width: 24, height: 24 }}
              disabled={disabled}
              onClick={() => {
                if (value > minValue) {
                  setValue(
                    Math.round((value - (type === "float" ? 0.1 : 1)) * 10) / 10
                  );
                }
              }}
              data-testid="minus-button"
            >
              <Text variant="body1">-</Text>
            </IconButton>
          </InputAdornment>
        ),
      }}
      size="small"
      sx={{
        width: 1,
        borderRadius: 3,
        "&.MuiInputBase-input": {
          textAlign: "center",
          border: `1px solid ${theme.customPalette.grey.white}`,
        },
        "& fieldset": {
          borderRadius: "10px",
          border: "none",
        },
        border: `1px solid ${theme.customPalette.grey.white}`,
        background: theme.customPalette.white.main,
        fontSize: FONT_SIZE,
        "& input": {
          flex: 1,
        },
        ...sx,
      }}
      type="number"
      value={value}
      variant={variant}
      inputProps={{
        style: { textAlign: "center", width: `${inputWidth}px` },
      }}
      onChange={(event) => {
        const newValue = parseFloat(event.target.value);
        if (newValue >= minValue && newValue <= maxValue) setValue(newValue);
        else if (newValue < minValue) {
          setValue(minValue);
        } else if (newValue > maxValue) setValue(maxValue);
      }}
      data-testid="number-input-field"
    />
  );
};

FloatInput.defaultProps = {
  variant: "outlined",
  max: Infinity,
  disabled: false,
};

export default FloatInput;
